import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as OperationsIcon,
  Inventory as ContainerIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigationCore } from '../../../core/router/navigation';
import { useCompanyCore } from '../../../core/modules/company';
import { useContainerCore } from '../../../core/modules/container';
import { useReceptionCore } from '../../../core/modules/reception';
import { useMasterCore } from '../../../core/modules/master';
import { toast } from 'react-toastify';
import { ContainerSearchDialog, ReceptionContainerSearchDialog, ReceptionContainerInfo } from '../../components/operations/containerClassification';
import { ContainerInfo } from '../../components/operations/containerClassification/ContainerInfo';
import { ProductSelection } from '../../components/operations/containerClassification/ProductSelection';
import { ReceptionContainer } from '../../../core/interfaces/receptionCotnainer.interface';
import { ContainerForClassification } from '../../../core/interfaces/container.interface';

export const ContainerClassificationScreen: React.FC = () => {
  const { goToWelcome } = useNavigationCore();
  const { companyId } = useCompanyCore();
  const { postProductToContainer, getContainers } = useContainerCore();
  const { getReceptionContainer } = useReceptionCore();
  const {
    productUsages,
    productPackagings,
    productFuntionalities,
    productAesthetics,
    productCompletenesses,
    productStates,
  } = useMasterCore();

  // Estado para los contenedores seleccionados
  const [originContainer, setOriginContainer] = useState<ReceptionContainer | null>(null);
  const [destinationContainer, setDestinationContainer] = useState<ContainerForClassification | null>(null);
  
  // Estado para el producto seleccionado
  const [productClassificationData, setProductClassificationData] = useState<any>(null);
  const [productResetKey, setProductResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estado para los diálogos de búsqueda
  const [originSearchOpen, setOriginSearchOpen] = useState(false);
  const [destinationSearchOpen, setDestinationSearchOpen] = useState(false);
  
  // Estado para los inputs de búsqueda
  const [originSearchInput, setOriginSearchInput] = useState('');
  const [destinationSearchInput, setDestinationSearchInput] = useState('');
  
  // Estado para el paso actual
  const [activeStep, setActiveStep] = useState(0);
  const originRef = useRef<HTMLDivElement | null>(null);
  const classificationRef = useRef<HTMLDivElement | null>(null);
  const destinationRef = useRef<HTMLDivElement | null>(null);

  const steps = [
    {
      label: 'Origen',
      description: 'Selecciona el contenedor de origen (recepción)',
    },
    {
      label: 'Producto y Clasificación',
      description: 'Selecciona producto y configura clasificación',
    },
    {
      label: 'Destino',
      description: 'Selecciona el contenedor de destino y confirma',
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setOriginContainer(null);
    setOriginSearchInput('');
    setDestinationContainer(null);
    setDestinationSearchInput('');
    setProductClassificationData(null);
    setProductResetKey((prev) => prev + 1);
  };

  const resetAfterClassification = () => {
    if (originContainer) {
      setActiveStep(1);
      setOriginSearchInput(originContainer.code || '');
    } else {
      setActiveStep(0);
    }
    setDestinationContainer(null);
    setDestinationSearchInput('');
    setProductClassificationData(null);
    setProductResetKey((prev) => prev + 1);
  };



  const handleProductClassificationSubmit = useCallback((data: any | null) => {
    setProductClassificationData(data);
  }, []);

  const handleClassificationSubmit = async () => {
    if (!originContainer || !destinationContainer || !productClassificationData) {
      console.error('Faltan datos para la clasificación');
      return;
    }

    // Validar cantidad
    if (!productClassificationData.quantity || productClassificationData.quantity <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    // Validar observaciones obligatorias
    const errors: string[] = [];
    
    // Validar observación de uso si requiere
    if (productClassificationData.usage_id) {
      const usageAttr = productUsages?.find(u => u.id === productClassificationData.usage_id);
      if (usageAttr?.has_description && !productClassificationData.usage_note?.trim()) {
        errors.push('La observación de uso es obligatoria');
      }
    }
    
    // Validar observación de completitud si requiere
    if (productClassificationData.completeness_id) {
      const completenessAttr = productCompletenesses?.find(c => c.id === productClassificationData.completeness_id);
      if (completenessAttr?.has_description && !productClassificationData.completeness_note?.trim()) {
        errors.push('La observación de completitud es obligatoria');
      }
    }
    
    // Validar observación de funcionalidad si requiere
    if (productClassificationData.functionality_id) {
      const functionalityAttr = productFuntionalities?.find(f => f.id === productClassificationData.functionality_id);
      if (functionalityAttr?.has_description && !productClassificationData.functionality_note?.trim()) {
        errors.push('La observación de funcionalidad es obligatoria');
      }
    }
    
    // Validar observación de embalaje si requiere
    if (productClassificationData.packaging_id) {
      const packagingAttr = productPackagings?.find(p => p.id === productClassificationData.packaging_id);
      if (packagingAttr?.has_description && !productClassificationData.packaging_note?.trim()) {
        errors.push('La observación de embalaje es obligatoria');
      }
    }
    
    // Validar observación de estado si requiere
    // Nota: El estado usa 'observation' como campo de observación según la definición
    if (productClassificationData.state_id) {
      const stateAttr = productStates?.find(s => s.id === productClassificationData.state_id);
      if (stateAttr?.has_description && !productClassificationData.observation?.trim()) {
        errors.push('La observación de estado es obligatoria');
      }
    }
    
    // Validar observación de estético si requiere
    if (productClassificationData.aesthetic_id) {
      const aestheticAttr = productAesthetics?.find(a => a.id === productClassificationData.aesthetic_id);
      if (aestheticAttr?.has_description && !productClassificationData.aesthetic_note?.trim()) {
        errors.push('La observación de estético es obligatoria');
      }
    }
    
    if (errors.length > 0) {
      toast.error(errors.join('. '));
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar los datos para el POST usando la interfaz existente
      const classificationPayload = {
        product_id: productClassificationData.product_id,
        width_cm: productClassificationData.width_cm,
        length_cm: productClassificationData.length_cm,
        depth_cm: productClassificationData.depth_cm,
        volumetric_weight_kg: productClassificationData.volumetric_weight_kg,
        weight_kg: productClassificationData.weight_kg,
        usage_note: productClassificationData.usage_note,
        packaging_note: productClassificationData.packaging_note,
        functionality_note: productClassificationData.functionality_note,
        aesthetic_note: productClassificationData.aesthetic_note,
        completeness_note: productClassificationData.completeness_note,
        observation: productClassificationData.observation,
        reference: productClassificationData.reference,
        quantity: productClassificationData.quantity,
        set_quantity: 0,
        is_set: false,
        usage_id: productClassificationData.usage_id,
        packaging_id: productClassificationData.packaging_id,
        functionality_id: productClassificationData.functionality_id,
        aesthetic_id: productClassificationData.aesthetic_id,
        completeness_id: productClassificationData.completeness_id,
        state_id: productClassificationData.state_id,
        set_complete: false,
        origin_container_id: originContainer.id
      };



      // Usar el servicio existente
      await postProductToContainer(companyId!, destinationContainer.id, classificationPayload);
      
      toast.success('Clasificación completada exitosamente');
      resetAfterClassification();
    } catch (error) {
      console.error('Error al enviar la clasificación:', error);
      toast.error('Error al enviar la clasificación. Revisa la consola para más detalles.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOriginContainerSelect = (container: ReceptionContainer) => {
    // Buscar el contenedor completo por código para obtener todos los datos (supplier, área, etc.)
    if (companyId && container.code) {
      getReceptionContainer(companyId, container.code, (fullContainer) => {
        if (fullContainer) {
          setOriginContainer(fullContainer);
          setOriginSearchInput(fullContainer.code);
        } else {
          // Fallback: usar el contenedor del modal si no se encuentra el completo
          setOriginContainer(container);
          setOriginSearchInput(container.code);
        }
        setOriginSearchOpen(false);
      });
    } else {
      setOriginContainer(container);
      setOriginSearchInput(container.code);
      setOriginSearchOpen(false);
    }
  };

  const handleDestinationContainerSelect = (container: ContainerForClassification) => {
    setDestinationContainer(container);
    setDestinationSearchInput(container.code);
    setDestinationSearchOpen(false);
  };

  // Handlers para los inputs de búsqueda
  const handleOriginInputChange = (value: string) => {
    setOriginSearchInput(value);
    // Aquí podrías agregar lógica de búsqueda automática si es necesario
  };

  const handleDestinationInputChange = (value: string) => {
    setDestinationSearchInput(value);
    // Aquí podrías agregar lógica de búsqueda automática si es necesario
  };

  // Búsqueda automática por código para contenedor de origen (recepción)
  useEffect(() => {
    if (!companyId) return;
    const value = originSearchInput.trim();
    if (!value) {
      setOriginContainer(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      getReceptionContainer(companyId, value, (container) => {
        if (container) {
          setOriginContainer(container);
        } else {
          setOriginContainer(null);
        }
      });
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [originSearchInput, companyId, getReceptionContainer]);

  // Búsqueda automática por código para contenedor de destino
  useEffect(() => {
    if (!companyId) return;
    const value = destinationSearchInput.trim();
    if (!value) {
      setDestinationContainer(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      getContainers(companyId, { code: value }, (containers: any[]) => {
        if (containers && containers.length === 1) {
          setDestinationContainer(containers[0]);
        } else {
          setDestinationContainer(null);
        }
      });
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [destinationSearchInput, companyId, getContainers]);

  const canProceedToNextStep = () => {
    return !!originContainer;
  };

  const canProceedToClassification = () => {
    if (!productClassificationData) return false;
    
    // Verificar que los campos requeridos estén completos
    const requiredFields = [
      'usage_id', 'packaging_id', 'functionality_id', 
      'aesthetic_id', 'completeness_id', 'state_id'
    ];
    
    const allRequiredFieldsFilled = requiredFields.every(field => 
      productClassificationData[field] && productClassificationData[field] > 0
    );
    
    if (!allRequiredFieldsFilled) return false;
    
    // Verificar que la cantidad sea mayor a 0
    if (!productClassificationData.quantity || productClassificationData.quantity <= 0) {
      return false;
    }
    
    // Verificar que las observaciones requeridas estén completas
    const attributeMappings = [
      { id: productClassificationData.usage_id, note: productClassificationData.usage_note, options: productUsages },
      { id: productClassificationData.packaging_id, note: productClassificationData.packaging_note, options: productPackagings },
      { id: productClassificationData.functionality_id, note: productClassificationData.functionality_note, options: productFuntionalities },
      { id: productClassificationData.aesthetic_id, note: productClassificationData.aesthetic_note, options: productAesthetics },
      { id: productClassificationData.completeness_id, note: productClassificationData.completeness_note, options: productCompletenesses },
      { id: productClassificationData.state_id, note: productClassificationData.observation, options: productStates },
    ];
    
    // Verificar que si un atributo requiere observación, la observación esté completa
    for (const mapping of attributeMappings) {
      if (mapping.id) {
        const selectedAttribute = mapping.options?.find(opt => opt.id === mapping.id);
        if (selectedAttribute?.has_description) {
          if (!mapping.note || mapping.note.trim() === '') {
            return false;
          }
        }
      }
    }
    
    return true;
  };

  const isStepCompleted = (index: number) => {
    if (index === 0) return !!originContainer;
    if (index === 1) return canProceedToClassification();
    if (index === 2) return !!destinationContainer;
    return false;
  };
  const workflowReady = isStepCompleted(0) && isStepCompleted(1) && isStepCompleted(2);

  const canNavigateTo = (index: number) => {
    // se puede navegar a un paso si todos los anteriores están completos
    for (let i = 0; i < index; i += 1) {
      if (!isStepCompleted(i)) return false;
    }
    return true;
  };

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const goToStep = (index: number) => {
    if (!canNavigateTo(index)) return;
    setActiveStep(index);
  };
  useEffect(() => {
    if (activeStep === 0) scrollToRef(originRef);
    if (activeStep === 1) scrollToRef(classificationRef);
    if (activeStep === 2) scrollToRef(destinationRef);
  }, [activeStep]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, position: 'sticky', top: 0, zIndex: 1000, backdropFilter: 'saturate(180%) blur(6px)', backgroundColor: 'rgba(255,255,255,0.9)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ContainerIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Clasificación Contenedor
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Ingresar artículos a un contenedor
        </Typography>

        {/* Stepper sticky en header */}
        <Box mt={2}>
          <Stepper activeStep={activeStep} orientation="horizontal">
            {steps.map((step, index) => (
              <Step key={step.label} completed={isStepCompleted(index)}>
                <StepLabel
                  onClick={() => goToStep(index)}
                  sx={{ cursor: canNavigateTo(index) ? 'pointer' : 'default' }}
                >
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="body2" fontWeight="medium">
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>

      {/* Contenido principal */}
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Paso 1: Origen */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={originRef}>
          <Typography variant="h6" color="primary" gutterBottom>
            Selección de contenedor de origen
          </Typography>
          
          <Grid container spacing={3}>
            {/* Contenedor de origen */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Contenedor de origen (Recepción)
                  </Typography>
                  
                  {originContainer ? (
                    <ReceptionContainerInfo 
                      container={originContainer} 
                      onClear={() => {
                        setOriginContainer(null);
                        setOriginSearchInput('');
                      }}
                    />
                  ) : (
                    <Box display="flex" gap={1} alignItems="center">
                      <TextField
                        placeholder="Escanear o buscar por código"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={originSearchInput}
                        onChange={(e) => handleOriginInputChange(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <IconButton
                        onClick={() => setOriginSearchOpen(true)}
                        size="small"
                        sx={{ 
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={() => { handleNext(); scrollToRef(classificationRef); }}
              disabled={!canProceedToNextStep()}
              endIcon={<ArrowForwardIcon />}
            >
              Continuar
            </Button>
          </Box>
        </Paper>

        {/* Separador visual */}
        <Divider />

        {/* Contenido del paso de Producto y Clasificación */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={classificationRef}>
          <Box sx={{ display: activeStep >= 1 ? 'block' : 'none' }}>
              <Typography variant="h6" gutterBottom>
                Producto y Clasificación
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Selecciona un producto y configura su clasificación para continuar.
              </Typography>
              
              {/* Información de contenedores seleccionados */}
              {/* <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Contenedores Seleccionados
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Origen:</strong> {originContainer?.code} - {originContainer?.reference}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Destino:</strong> {destinationContainer?.code} - {destinationContainer?.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card> */}

              {/* Componente de selección de producto */}
              <ProductSelection
                onProductSelect={handleProductClassificationSubmit}
                companyId={companyId || undefined}
                resetKey={productResetKey}
              />
              
              <Box display="flex" justifyContent="space-between" mt={3}>
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      // Solo resetear los datos de clasificación, mantener el producto
                      setProductResetKey((prev) => prev + 1);
                    }}
                    disabled={!productClassificationData}
                  >
                    Limpiar Clasificación
                  </Button>
                  <Button onClick={handleBack}>
                    Atrás
                  </Button>
                </Box>
                {activeStep < 2 && (
                  <Button
                    variant="contained"
                    onClick={() => { handleNext(); scrollToRef(destinationRef); }}
                    disabled={!canProceedToClassification()}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Continuar
                  </Button>
                )}
              </Box>
          </Box>
        </Paper>

        {/* Paso 3: Destino */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={destinationRef}>
          <Box sx={{ display: activeStep >= 2 ? 'block' : 'none' }}>
              <Typography variant="h6" gutterBottom>
                Destino
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Selecciona el contenedor de destino y confirma la clasificación.
              </Typography>

              

              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Contenedor de destino
                      </Typography>
                      {destinationContainer ? (
                        <ContainerInfo 
                          container={destinationContainer} 
                          onClear={() => {
                            setDestinationContainer(null);
                            setDestinationSearchInput('');
                          }}
                        />
                      ) : (
                        <Box display="flex" gap={1} alignItems="center">
                          <TextField
                            placeholder="Escanear o buscar por código"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={destinationSearchInput}
                            onChange={(e) => handleDestinationInputChange(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <IconButton
                            onClick={() => setDestinationSearchOpen(true)}
                            size="small"
                            sx={{ 
                              border: '1px solid #ccc',
                              borderRadius: 1,
                              backgroundColor: 'white',
                              '&:hover': {
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          >
                            <FilterListIcon />
                          </IconButton>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-start" mt={3}>
                <Button onClick={handleBack}>Atrás</Button>
              </Box>
            </Box>
        </Paper>
      </Box>

      {/* Botón flotante Clasificar (sin layout de fondo) */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 24, zIndex: 1300 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClassificationSubmit}
          disabled={!workflowReady || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Clasificando...' : 'Clasificar'}
        </Button>
      </Box>

      {/* Diálogos de búsqueda */}
      <ReceptionContainerSearchDialog
        open={originSearchOpen}
        onClose={() => setOriginSearchOpen(false)}
        onSelect={handleOriginContainerSelect}
        title="Buscar contenedor de origen (Recepción)"
        companyId={companyId || undefined}
      />

      <ContainerSearchDialog
        open={destinationSearchOpen}
        onClose={() => setDestinationSearchOpen(false)}
        onSelect={handleDestinationContainerSelect}
        title="Buscar contenedor de destino"
        companyId={companyId || undefined}
      />
    </Container>
  );
};
